import { FunctionComponent, RefObject, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import listPlugin from '@fullcalendar/list'
import styles from './calendarDesktop.module.scss'
import { DateSelectArg, EventClickArg, EventContentArg, MoreLinkContentArg } from '@fullcalendar/core'
import moment, { Moment } from 'moment-timezone'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import { ArrowBack, ArrowForward } from '@mui/icons-material'
import { CalendarIcon } from '@mui/x-date-pickers'
import DatePickerDialog from '../../Dialog/datePicker/datePickerDialog.tsx'
import { calendarViews } from '../../utils/calendarParameters.ts'
import {
    compareEventsByType,
    compareEventsForParents,
    renderEventContent,
    renderEventGlobalRate,
    renderMoreLinkContent,
    stringToCalendarView
} from '../../utils/calendarEvents.tsx'
import ButtonIcon from '@/components/_custom/Buttons/ButtonIcon/ButtonIcon.tsx'
import { Daycare } from '@/types/Daycare.ts'
import { EventCalendar } from '@/types/Calendar.ts'
import allLocales from '@fullcalendar/core/locales-all'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper.ts'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import ShowMoreLinkDialog from '../../Dialog/moreLinkDialog/showMoreLinkDialog.tsx'
import GlobalPlanningNotConfigured from '../components/GlobalPlanningNotConfigured.tsx'
import { getDraftingRoute, useMoreLinkDialog, useRemoveFcPopover } from '../hooks/useCalendarNavigation.ts'
import { useCalendarControls } from '../hooks/useCalendarControls.ts'
import { filterMoreLinkEvents, mapEventsWithClassName } from '../utils/calendarHelpers.ts'
import { FetchPlanningDataProps } from '../../hooks/usePlanningSlots.ts'

interface CalendarWebProps {
    currentView: string
    setCurrentView: (view: string) => void
    fetchData: (props: FetchPlanningDataProps) => void
    isParent?: boolean
    calendarEvents: EventCalendar[]
    currentDaycare: Daycare
    hiddenDays: number[]
    handleSelect: (info: DateSelectArg) => void
    handleEventClickArg: (info: EventClickArg) => void
    handleEventClick: (info: EventCalendar) => void
    isGlobalPlanning: boolean
}

const CalendarDesktop: FunctionComponent<CalendarWebProps> = ({
    currentView,
    setCurrentView,
    fetchData,
    isParent = false,
    calendarEvents,
    currentDaycare,
    hiddenDays,
    handleSelect,
    handleEventClickArg,
    handleEventClick,
    isGlobalPlanning
}: CalendarWebProps) => {
    const { t } = useTranslation()
    const navigate: NavigateFunction = useNavigate()
    const calendarRef: RefObject<FullCalendar | null> = useRef(null)

    const [isDatePickerDialogOpen, setDatePickerDialogOpen] = useState(false)
    const [events, setEvents] = useState<EventCalendar[]>()

    useRemoveFcPopover()

    const { handlePrev, handleNext, setNewCalendarTitle, calendarTitle, selectedDate, setSelectedDate } =
        useCalendarControls({
            calendarRef,
            currentView,
            setCurrentView,
            fetchData,
            isGlobalPlanning,
            isMobile: false
        })

    const {
        openMoreLinkDialog,
        setOpenMoreLinkDialog,
        moreLinkEvents,
        setMoreLinkEvents,
        moreLinkDate,
        handleMoreLinkClick
    } = useMoreLinkDialog()

    useEffect(() => {
        setEvents(mapEventsWithClassName(calendarEvents, styles.EventCell))

        if (moreLinkEvents && moreLinkEvents.length > 0 && calendarEvents.length > 0) {
            setMoreLinkEvents(filterMoreLinkEvents(calendarEvents, moreLinkDate, styles.EventCell))
        }
    }, [calendarEvents])

    useEffect(() => {
        if (calendarRef.current) {
            const start: Moment = moment(calendarRef.current.getApi().view.activeStart)
            const end: Moment = moment(calendarRef.current.getApi().view.activeEnd).subtract(1, 'd')

            fetchData({ fromDate: start, toDate: end, isGlobalPlanning })
        }
    }, [currentDaycare, isGlobalPlanning])

    const isGlobalPlanningNotConfigured: boolean =
        isGlobalPlanning && (currentDaycare.sectionsGroups === undefined || currentDaycare.sectionsGroups.length === 0)

    return (
        <div className={styles.CalendarHandler}>
            {isGlobalPlanningNotConfigured ? (
                <GlobalPlanningNotConfigured daycareId={currentDaycare.id} />
            ) : (
                <div className={styles.CalendarAndHeader}>
                    <div className={styles.Header}>
                        <ButtonTextIcon
                            type={'custom'}
                            onClick={handlePrev}
                            customText={' '}
                            customIcon={<ArrowBack />}
                            parent={isParent}
                        />
                        <div className={styles.HeaderTitle}>
                            <p className={styles.Title}>{calendarTitle}</p>
                            <ButtonIcon
                                icon={<CalendarIcon />}
                                onClick={() => setDatePickerDialogOpen(true)}
                                parent={isParent}
                                className={styles.Icon}
                            />
                        </div>
                        <ButtonTextIcon
                            type={'custom'}
                            onClick={handleNext}
                            customText={' '}
                            customIcon={<ArrowForward />}
                            parent={isParent}
                        />
                    </div>
                    <div className={styles.Calendar}>
                        <FullCalendar
                            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                            headerToolbar={false}
                            datesSet={setNewCalendarTitle}
                            selectable={true}
                            initialView={currentView}
                            views={calendarViews}
                            firstDay={1}
                            select={handleSelect}
                            events={events}
                            dayCellClassNames={styles.Cell}
                            dayHeaderClassNames={`${isParent ? styles.HeaderCellParent : styles.HeaderCellDaycare}`}
                            height={'100%'}
                            locales={allLocales}
                            locale={getLocalFromLanguage()}
                            hiddenDays={hiddenDays}
                            eventClick={(e: EventClickArg) =>
                                isGlobalPlanning
                                    ? navigate(
                                          getDraftingRoute(
                                              e.event.extendedProps.sectionGroupId,
                                              moment(e.event.start).format('DD-MM-YYYY')
                                          )
                                      )
                                    : handleEventClickArg(e)
                            }
                            ref={calendarRef}
                            eventContent={(eventInfo: EventContentArg) =>
                                isGlobalPlanning
                                    ? renderEventGlobalRate(eventInfo, stringToCalendarView(currentView))
                                    : renderEventContent(eventInfo, stringToCalendarView(currentView))
                            }
                            moreLinkClassNames={`${isParent ? styles.MoreLinkParent : styles.MoreLinkDaycare}`}
                            noEventsClassNames={`${isParent ? styles.NoEventsParent : styles.NoEventsDaycare}`}
                            displayEventTime={false}
                            moreLinkContent={(info: MoreLinkContentArg) => renderMoreLinkContent(info, false, t)}
                            allDayClassNames={styles.AllDay}
                            eventOrder={isParent ? compareEventsForParents : compareEventsByType}
                            moreLinkClick={handleMoreLinkClick}
                        />
                    </div>
                    <DatePickerDialog
                        open={isDatePickerDialogOpen}
                        handleClose={() => setDatePickerDialogOpen(false)}
                        selectedDate={selectedDate}
                        handleDateChange={setSelectedDate}
                        parent={isParent}
                        currentDaycare={currentDaycare}
                    />
                    <ShowMoreLinkDialog
                        open={openMoreLinkDialog}
                        handleClose={() => setOpenMoreLinkDialog(false)}
                        events={moreLinkEvents}
                        date={moreLinkDate}
                        isParent={isParent}
                        handleEventClick={handleEventClick}
                        isGlobalPlanning={isGlobalPlanning}
                    />
                </div>
            )}
        </div>
    )
}

export default CalendarDesktop
